import {skipToken, useInfiniteQuery, useMutation, useQuery} from '@tanstack/react-query';
import {Creative} from 'bigdatr-style';
import {SupportedCountry} from 'bigdatr-style';
import {CreativeListOrderBy} from '~/feature/filter/CreativeSortBy';
import {QueryStringFilters} from '~/feature/filter/types';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {
    CreativeList,
    GroupByCampaign,
    PublicCreativeItem,
    SingleCreativeMediaValue
} from './graphqlQueries';
import {CreativeDownload} from './graphqlQueries';
import ensureDatesAreWithinTrialLimits from '~/creative/util/ensureDatesAreWithinTrialLimits';
import {useCountry} from '~/feature/country/CountryContext';
import useEarliestSelectableCreativeDate from '~/affordance/dates/useEarliestSelectableDate';
import Campaign from '~/creative/Campaign';

type payload = {
    queryFilters?: QueryStringFilters;
    orderBy?: CreativeListOrderBy;
    pageNumber?: number;
    pageSize?: number;
    requestKey?: string;
    options?: {enabled: boolean};
};

type mediaDownload = {
    creativeList: string[];
    downloadFilename?: string;
    options?: {enabled: boolean};
};

export function useSingleCreative(args: {id: string}, options?: {enabled: boolean}) {
    const {id} = args;
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;
    return useQuery({
        queryKey: ['singleCreative', id],
        queryFn: enabled
            ? async () => {
                  const response = await request(PublicCreativeItem, {creativeId: id});
                  const rawCreative = response.creativeV2?.publicCreativeItemV2;
                  if (!rawCreative) throw new Error('Creative not returned from api');

                  return new Creative(rawCreative);
              }
            : skipToken
    });
}

export function useInfiniteCreativeList(
    args: {
        filters?: QueryStringFilters;
        orderBy?: CreativeListOrderBy;
        pageNumber?: number;
        country?: SupportedCountry;
    },
    options?: {enabled: boolean}
) {
    const {orderBy} = args;
    const pageSize = 20;
    const pageNumber = args.pageNumber ?? 1;

    const {creativeDateRange, ...otherFilters} = args.filters ?? {};
    const filters = {...otherFilters, country: args.country, dateRangeFilter: creativeDateRange};

    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;
    return useInfiniteQuery({
        queryKey: ['creativeList', filters, orderBy, pageSize, pageNumber],
        queryFn: enabled
            ? async ({pageParam}) => {
                  const response = await request(CreativeList, {
                      input: {
                          filters,
                          orderBy,
                          pagination: {
                              count: pageSize,
                              page: pageParam
                          }
                      }
                  });
                  const rawResponse = response.creativeV2?.creativeListV2;
                  if (!rawResponse) throw new Error('Creative list not returned from api');

                  const {items, totalCount, paginationInfo} = rawResponse;

                  return {
                      totalCount,
                      paginationInfo,
                      items: items.map((rawCreative) => new Creative(rawCreative))
                  };
              }
            : skipToken,
        initialPageParam: pageNumber,
        getNextPageParam: (lastPage) => {
            const hasNextPage = lastPage.paginationInfo?.hasNextPage;
            const currentPage = lastPage.paginationInfo?.currentPage;
            if (hasNextPage && currentPage) return currentPage + 1;
            return null;
        },
        getPreviousPageParam: (lastPage) => {
            const hasPreviousPage = lastPage.paginationInfo?.hasPreviousPage;
            const currentPage = lastPage.paginationInfo?.currentPage;
            // dont allow going to page 0
            if (hasPreviousPage && currentPage) return currentPage - 1;
            return null;
        }
    });
}

export function useSingleCreativeMediaValue(args: {id: string}) {
    const {id: creativeId} = args;
    const request = useClientMainAuthorizedFetch();
    return useQuery({
        queryKey: ['singleCreativeMediaValue', creativeId],
        queryFn: async () => {
            const response = await request(SingleCreativeMediaValue, {input: {creativeId}});
            const mediaValue = response.creativeV2?.creativeMediaValue;
            if (!mediaValue) throw new Error('Creative media value not returned from api');
            return mediaValue;
        }
    });
}

export function useFetchCreativeList(payload: payload) {
    const {country, isCreativeOnTrial} = useCountry();
    const earliestSelectableDate = useEarliestSelectableCreativeDate({type: 'creative'});
    const enabled = payload.options?.enabled ?? true;
    const {creativeDateRange, ...otherFilters} = payload.queryFilters ?? {};

    const dateWithinLimitations = ensureDatesAreWithinTrialLimits({
        originalDate: creativeDateRange,
        isTrialing: isCreativeOnTrial,
        earliestSelectableDate
    });

    const filters = {
        ...otherFilters,
        country: country,
        dateRangeFilter: dateWithinLimitations
    };
    const request = useClientMainAuthorizedFetch();
    return useQuery({
        queryKey: [
            'allCreativeList',
            filters,
            payload.orderBy,
            payload.pageSize,
            payload.pageNumber,
            payload.requestKey
        ],
        queryFn: enabled
            ? async () => {
                  const response = await request(CreativeList, {
                      input: {
                          filters: filters,
                          orderBy: payload.orderBy,
                          pagination: {
                              count: payload.pageSize ?? 20,
                              page: payload.pageNumber ?? 1
                          }
                      }
                  });
                  const rawResponse = response.creativeV2?.creativeListV2;
                  if (!rawResponse) throw new Error('Creative list not returned from api');

                  const {items, totalCount, paginationInfo} = rawResponse;

                  return {
                      totalCount,
                      paginationInfo,
                      items: items.map((rawCreative) => new Creative(rawCreative))
                  };
              }
            : skipToken
    });
}

export function useCreativeDownload() {
    const request = useClientMainAuthorizedFetch();

    return useMutation({
        mutationFn: async (payload: mediaDownload) => {
            const response = await request(CreativeDownload, {
                input: {
                    creativeList: payload.creativeList,
                    downloadFilename: payload.downloadFilename ?? ''
                }
            });
            const rawResponse = response.creativeV2?.creativeDownload;
            if (!rawResponse) throw new Error('Creative download not returned from api');

            return rawResponse;
        }
    });
}

export function useGroupByCampaign(
    input: {
        filters: QueryStringFilters;
        orderBy: CreativeListOrderBy;
        afterKey?: string;
    },
    options?: {enabled: boolean}
) {
    const request = useClientMainAuthorizedFetch();
    const {country} = useCountry();
    const {orderBy} = input;

    const {creativeDateRange, ...otherFilters} = input.filters ?? {};
    const filters = {...otherFilters, country, dateRangeFilter: creativeDateRange};

    // an empty string is technically a cursor in itself, however a nice sideeffect is that this
    // will filter out the campaigns who have an empty string as their campaign
    const initialAfterKey = input.afterKey || '';
    const pageSize = 20;

    const enabled = options?.enabled ?? true;
    return useInfiniteQuery({
        queryKey: ['groupByCampaign', filters, orderBy, initialAfterKey, pageSize],
        queryFn: enabled
            ? async ({pageParam}) => {
                  const response = await request(GroupByCampaign, {
                      input: {filters, orderBy, pagination: {afterKey: pageParam, pageSize}}
                  });
                  const data = response.creativeV2?.groupByCampaign;
                  if (!data) throw new Error('Group by campaign data not returned from api');

                  return {
                      items: data.items.map((item) => new Campaign(item)),
                      paginationInfo: data.paginationInfo
                  };
              }
            : skipToken,
        initialPageParam: initialAfterKey,
        getNextPageParam: (lastPage) => {
            const {afterKey, nextPageMayContainMoreCampaigns} = lastPage.paginationInfo ?? {};
            if (nextPageMayContainMoreCampaigns && afterKey) return afterKey;
            return null;
        }
    });
}
